/**
 * Un cuadro del vídeo como imagen, para la portada.
 *
 * Al buscar un instante, el evento `seeked` puede llegar antes de que ese cuadro
 * esté pintado, y dibujarlo entonces da un rectángulo negro (pasó: portadas
 * negras de vídeos que no empiezan en negro). Se espera al cuadro de verdad
 * (`requestVideoFrameCallback`, o un instante si no existe) y, si aun así sale
 * casi negro, se prueba más adelante.
 */
export function videoPosterFile(file, at) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        /* Montado en la página, invisible: fuera del documento, Safari (y a veces
           Chrome) no pinta los cuadros y el dibujo sale transparente —negro al
           guardarlo—. Con `opacity` y no `display: none`, que también lo apaga. */
        video.setAttribute('aria-hidden', 'true');
        video.style.cssText = 'position:fixed;left:-9999px;top:0;width:320px;height:180px;opacity:0;pointer-events:none';
        document.body.appendChild(video);
        let finished = false;
        const finish = (result) => {
            if (finished)
                return;
            finished = true;
            window.clearTimeout(timer);
            URL.revokeObjectURL(url);
            video.removeAttribute('src');
            video.remove();
            if (result instanceof Error)
                reject(result);
            else
                resolve(result);
        };
        const timer = window.setTimeout(() => finish(new Error('poster timeout')), 20000);
        let times = [];
        let tryIndex = 0;
        const scale = () => Math.min(1, 1600 / (video.videoWidth || 1600));
        const capture = () => {
            const canvas = document.createElement('canvas');
            canvas.width = Math.round((video.videoWidth || 1600) * scale());
            canvas.height = Math.round((video.videoHeight || 900) * scale());
            const context = canvas.getContext('2d');
            if (!context) {
                finish(new Error('poster canvas'));
                return;
            }
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            /* ¿Casi negro? Una muestra pequeña basta para saberlo. */
            const sample = document.createElement('canvas');
            sample.width = 32;
            sample.height = 18;
            const sampleContext = sample.getContext('2d');
            let dark = false;
            if (sampleContext) {
                sampleContext.drawImage(canvas, 0, 0, 32, 18);
                const pixels = sampleContext.getImageData(0, 0, 32, 18).data;
                let total = 0;
                for (let i = 0; i < pixels.length; i += 4)
                    total += (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
                dark = total / (pixels.length / 4) < 10;
            }
            if (dark && tryIndex < times.length - 1) {
                tryIndex += 1;
                video.currentTime = times[tryIndex];
                return;
            }
            canvas.toBlob((blob) => {
                if (!blob) {
                    finish(new Error('poster blob'));
                    return;
                }
                finish(new File([blob], file.name.replace(/\.[^.]+$/, '') + '-portada.jpg', { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.86);
        };
        /* Con datos ya cargados: un instante de reproducción (sin sonido) hace que
           el navegador empiece a decodificar cuadros; luego se pausa y se busca. */
        video.onloadeddata = () => {
            const duration = video.duration || 0;
            const later = (seconds) => Math.min(seconds, Math.max(0, duration - 0.05));
            times = at === 'start'
                /* El primer cuadro exacto: 0 no sirve (si ya está ahí no hay `seeked`). */
                ? [0.001, later(0.5), later(1), later(duration / 3)]
                : [later(Math.min(1, duration / 3)), later(duration / 2), 0.001];
            const seek = () => { video.pause(); video.currentTime = times[0]; };
            /* `play()` devuelve una promesa (en navegadores viejos, nada): se busca al
               resolverse o al fallar, y si no hay promesa, enseguida. */
            Promise.resolve(video.play()).then(seek, seek);
        };
        video.onseeked = () => {
            /* Esperar a que el cuadro esté pintado antes de dibujarlo. */
            const withFrame = video;
            if (typeof withFrame.requestVideoFrameCallback === 'function') {
                let called = false;
                const once = () => { if (!called) {
                    called = true;
                    capture();
                } };
                withFrame.requestVideoFrameCallback(once);
                /* Un vídeo en pausa no siempre presenta un cuadro nuevo: por si acaso. */
                window.setTimeout(once, 400);
            }
            else {
                window.setTimeout(capture, 120);
            }
        };
        video.onerror = () => finish(new Error('poster decode'));
        video.src = url;
    });
}
//# sourceMappingURL=videoPoster.js.map